import { makeStyles, shorthands } from "@fluentui/react-components";
import React, { useEffect, useState } from "react";

export interface TeamyEIDExtensionWindow extends Window {
    TeamyEIDExtension: any;
}

export const ID_FLAGS = {
    ID_FLAG_INCLUDE_ID: 1,
    ID_FLAG_INCLUDE_ADDR: 2,
    ID_FLAG_INCLUDE_PHOTO: 4,
    ID_FLAG_INCLUDE_INTEGRITY: 8,
    ID_FLAG_INCLUDE_CERTS: 16,
    ID_FLAG_INCLUDE_AUTH_CERT: 32,
    ID_FLAG_INCLUDE_SIGN_CERT: 64,
    ID_FLAG_INCLUDE_CACERTS: 128,
    ID_FLAG_INCLUDE_ROOTCERT: 256
};

let EIDApiInstance: any | null = null;

const GetEIDApiInstance = (): any | null => {
    if ("TeamyEIDExtension" in window && EIDApiInstance === null) {
        const AnyWindow = window as any;
        EIDApiInstance = new AnyWindow.TeamyEIDExtension();
    }
    return EIDApiInstance;
};

function base64ToArrayBuffer(base64: string) {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes;
}
// https://stackoverflow.com/questions/44471479/from-my-website-programmatically-initiate-the-installation-of-a-chrome-extensio

interface IDFile {
    cardNumber?: string;
    cardValidityDateBeginString?: string;
    cardValidityDateEndString?: string;
    cardDeliveryMunicipality?: string;
    nationalNumber?: string;
    name?: string;
    TwoFirstGivenNames?: string;
    firstLetterOf3rdGivenName?: string;
    nationality?: string;
    birthLocation?: string;
    birthDate?: string;
    sex?: string;
    nobleCondition?: string;
    documentType?: string;
    specialStatus?: string;
    duplicate?: string;
    specialOrganisation?: string;   // 1: SHAPE, 2: NATO, 4: Old European blue card, 5: Researche
    dateAndCountryOfProtection?: string;
    mentions?: string;
    VATNumber1?: string;
    VATNumber2?: string;
    regionalFileNumber?: string;
}

function DecodeIDFile(base64: string): IDFile {
    const decodedId = base64ToArrayBuffer(base64);
    let pos = 0;
    const idfile: IDFile = {};
    while (pos < decodedId.byteLength) {
        const type = decodedId[pos];
        pos++;
        const len = decodedId[pos];
        pos++;
        if (len > 0) {
            switch (type) {
                case 1:
                    idfile.cardNumber = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 3:
                    idfile.cardValidityDateBeginString = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 4:
                    idfile.cardValidityDateEndString = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 5:
                    idfile.cardDeliveryMunicipality = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 6:
                    idfile.nationalNumber = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 7:
                    idfile.name = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 8:
                    idfile.TwoFirstGivenNames = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 9:
                    idfile.firstLetterOf3rdGivenName = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 10:
                    idfile.nationality = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 11:
                    idfile.birthLocation = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 12:
                    idfile.birthDate = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 13:
                    idfile.sex = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 14:
                    idfile.nobleCondition = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 15:
                    idfile.documentType = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 16:
                    idfile.specialStatus = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 18:
                    idfile.duplicate = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 19:
                    idfile.specialOrganisation = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 21:
                    idfile.dateAndCountryOfProtection = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 22:
                    idfile.mentions = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 23:
                    idfile.VATNumber1 = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 24:
                    idfile.VATNumber2 = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 25:
                    idfile.regionalFileNumber = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
            }
        }
        pos += len;
    }
    return idfile;
}

interface AddressFile {
    streetAndNumber?: string;
    zipCode?: string;
    municipality?: string;
}

function DecodeAddressFile(base64: string): AddressFile {
    const decodedId = base64ToArrayBuffer(base64);
    let pos = 0;
    const addressFile: AddressFile = {};
    while (pos < decodedId.byteLength) {
        const type = decodedId[pos];
        pos++;
        const len = decodedId[pos];
        pos++;
        if (len > 0) {
            switch (type) {
                case 1:
                    addressFile.streetAndNumber = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 2:
                    addressFile.zipCode = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
                case 3:
                    addressFile.municipality = new TextDecoder().decode(decodedId.slice(pos, pos + len));
                    break;
            }
        }
        pos += len;
    }
    return addressFile;
}

interface Smartcard {
    id: string;
    readerName: string;
    cardType: string;
    idFile?: IDFile;
    addressFile?: AddressFile;
    photo?: string;
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        width: "100%",
        flexDirection: 'column',
        ...shorthands.overflow("auto"),
    },
});

export const EID = () => {
    const [teamyEIDExtensionDetected, setTeamyEIDExtensionDetected] = useState(false);
    const [cards, setCards] = useState<Smartcard[]>([]);
    const styles = useStyles();
    useEffect(() => {
        if ("TeamyEIDExtension" in window) {
            console.log("TeamyEIDExtension detected");
            try {
                const api = GetEIDApiInstance();
                if (api) {
                    api.checkVersion("1.0", // minimum beiconnect version
                        function (installedVersion: any) {
                            console.log("TeamyEIDExtension natif app correct version");
                            console.log(installedVersion);
                            setTeamyEIDExtensionDetected(true);
                        },
                        function (msg: any) {
                            console.log("TeamyEIDExtension natif app not installed");
                            console.log(msg);
                        },
                        function (installedVersion: any) {
                            console.log("TeamyEIDExtension natif app installed, but need update");
                            console.log(installedVersion);
                        }
                    );
                }
            }
            catch (error) {
                console.log("Error catch TeamyEIDExtension", error);
            }
        }
        else {
            console.log("TeamyEIDExtension not detected");
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const api = GetEIDApiInstance();
            api.getId(
                'en',
                "0123456789ABCDEF0123456789ABCDEF",
                ID_FLAGS.ID_FLAG_INCLUDE_ID
            )
                .then((response: any) => {
                    if (response.result && response.result === "OK") {
                        if (response.Readers && response.Readers.length && response.Readers.length > 0) {
                            // Check existant
                            let changeDetected: boolean = response.Readers.length !== cards.length;
                            if (!changeDetected) {
                                cards.forEach((sc) => {
                                    if (response.Readers.findIndex((s: any) => s.id === sc.id && s.ReaderName === sc.readerName) === -1) {
                                        changeDetected = true;
                                    }
                                });
                            }
                            console.log("cards", cards);
                            console.log("changeDetected", changeDetected);

                            if (changeDetected) {
                                console.log("Get smart card details");
                                api.getId(
                                    'en',
                                    "0123456789ABCDEF0123456789ABCDEF",
                                    ID_FLAGS.ID_FLAG_INCLUDE_ID |
                                    ID_FLAGS.ID_FLAG_INCLUDE_PHOTO |
                                    ID_FLAGS.ID_FLAG_INCLUDE_ADDR
                                )
                                    .then((response: any) => {
                                        setCards(
                                            response.Readers.map((s: any): Smartcard => {
                                                const sc: Smartcard = { readerName: s.ReaderName, cardType: s.cardType, id: s.id };
                                                try {
                                                    sc.idFile = DecodeIDFile(s.id);
                                                }
                                                catch (error) { console.log(error); }
                                                try {
                                                    sc.photo = s.photo;
                                                }
                                                catch (error) { console.log(error); }
                                                try {
                                                    sc.addressFile = DecodeAddressFile(s.addr);
                                                }
                                                catch (error) { console.log(error); }
                                                return sc;
                                            }));
                                    });
                            }
                        }
                    }
                    else if (response.result && response.result === "no_card") {
                        setCards([]);
                    }
                })
                .catch((err: any) => {
                    setCards([]);
                    console.log("TeamyEIDExtension", err);
                });
        }, 5000);

        return () => {
            // setInterval cleared when component unmounts
            clearInterval(interval);
        };
    });

    //{teamyEIDExtensionDetected ? "Extension detect" : "Extension not detect"}

    //if (window.chrome) {
    //    chrome.webstore.install(url, successCallback, failureCallback)
    //}

    console.log("cards", cards);
    return <div>
        {"chrome" in window && <>window.chrome detected</>}
        {
            cards.map((s) =>
                <div className={styles.root} >
                    {s.readerName && <pre>Reader                : {s.readerName}</pre>}
                    {s.photo && <img src={`data:image/jpeg;base64,${s.photo}`} height="200px" width="140px" />}
                    {s.idFile && <>
                        {s.idFile.cardNumber &&
                            <pre>Card Number                : {s.idFile.cardNumber}</pre>}
                        {s.idFile.cardValidityDateBeginString &&
                            <pre>Card Validity Date Begin   : {s.idFile.cardValidityDateBeginString}</pre>}
                        {s.idFile.cardValidityDateEndString &&
                            <pre>Card Validity Date End     : {s.idFile.cardValidityDateEndString}</pre>}
                        {s.idFile.cardDeliveryMunicipality &&
                            <pre>Card Delivery Municipality : {s.idFile.cardDeliveryMunicipality}</pre>}
                        {s.idFile.TwoFirstGivenNames &&
                            <pre>2 First Given Names        : {s.idFile.TwoFirstGivenNames}</pre>}
                        {s.idFile.name &&
                            <pre>Name                       : {s.idFile.name}</pre>}
                        {s.idFile.nationalNumber &&
                            <pre>National Number            : {s.idFile.nationalNumber}</pre>}
                        {s.idFile.nationality &&
                            <pre>Nationality                : {s.idFile.nationality}</pre>}
                        {s.idFile.birthLocation &&
                            <pre>Birth Location             : {s.idFile.birthLocation}</pre>}
                        {s.idFile.birthDate &&
                            <pre>Birth Date                 : {s.idFile.birthDate}</pre>}
                        {s.idFile.sex &&
                            <pre>Sex                        : {s.idFile.sex}</pre>}
                    </>}
                    {s.addressFile && <>
                        {s.addressFile.streetAndNumber &&
                            <pre>street + Number            : {s.addressFile.streetAndNumber}</pre>}
                        {s.addressFile.zipCode &&
                            <pre>Zip Code                   : {s.addressFile.zipCode}</pre>}
                        {s.addressFile.municipality &&
                            <pre>Municipality               : {s.addressFile.municipality}</pre>}
                    </>}
                </div>
            )
        }
    </div>;
};